// client/src/pages/MyOrders.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  Box, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Chip, Alert, CircularProgress,
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { formatCurrency } from '../utils/currency';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';

const statusColor = {
  Pending: 'warning',
  Shipped: 'info',
  Completed: 'success',
  Cancelled: 'default',
};

const paymentColor = { unpaid: 'warning', paid: 'success', failed: 'error' };

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useContext(AuthContext);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/orders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch orders.');
        const data = await response.json();
        setOrders(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [token]);

  if (loading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>My Orders</Typography>
      {orders.length === 0 ? (
        <EmptyState
          icon={<ReceiptLongOutlinedIcon />}
          title="You have not placed any orders yet"
          description="Items you buy will show up here so you can track their status."
          actionLabel="Start Shopping"
          actionTo="/"
        />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Fulfillment</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell><Link to={`/orders/${order.id}`}>#{order.id}</Link></TableCell>
                  <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                  <TableCell><Chip label={order.fulfillment_type === 'collection' ? 'Collection' : 'Delivery'} size="small" variant="outlined" /></TableCell>
                  <TableCell><Chip label={order.status} color={statusColor[order.status] || 'default'} size="small" /></TableCell>
                  <TableCell><Chip label={order.payment_status} color={paymentColor[order.payment_status] || 'default'} size="small" sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell align="right">{formatCurrency(order.total_amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default MyOrders;
